import React, { useState, useContext } from "react";
import "./ChatWidget.css";
import { Context } from "../../index"; // Import MobX context (or replace with your state management)
import {$host} from "../../http";

const ChatWidget = ({ onClose }) => {
    const { user } = useContext(Context); // Get user info from MobX store
    const [messages, setMessages] = useState([{ text: "How can I assist you?", sender: "bot" }]);
    const [input, setInput] = useState("");

    const sendMessage = async () => {
        if (!input.trim()) return;

        const newMessage = { text: input, sender: "user" };
        setMessages([...messages, newMessage]);
        setInput("");

        try {
            const { data } = await $host.post("api/ai/chat-completions", {
                model: "openai/gpt-4o-mini-2024-07-18",
                messages: [
                        {
                            role: "system",
                            content: `You are Virtex, the AI assistant for Virtalibry, an online book review platform. Your role is to provide accurate and friendly support regarding Virtalibry’s services. Here are your key instructions:

**📌 General Rules:**
- Always maintain a professional, helpful, and neutral tone.
- Provide clear and accurate responses based on the FAQ and system guidelines.
- Do NOT discuss topics unrelated to Virtalibry (e.g., politics, personal advice, unrelated tech support).
- If a user asks about something outside your scope, politely redirect them to Virtalibry's support team.
- Never make guarantees about review outcomes or Amazon ranking improvements.

**🎯 Your Responsibilities:**
- Explain Virtalibry’s **subscription plans, limits, and features**.
- Guide users on **how to submit books, earn Virts, and receive Amazon reviews**.
- Clarify **how review assignments work** (including Verified Purchase, Search & Buy, and Photo Reviews).
- Help users with **account management**, **plan upgrades/downgrades**, and **subscription cancellation**.
- Ensure authors understand **how reviews are assigned and the Virts system works**.
- Provide answers about **review verification on Amazon and platform security**.

**🔑 Key User Information:**
- Name: ${user.user?.username || "Guest"}
- Plan: ${user.user?.tariff_plan || "Free"}
- Assignment Limit: ${user.user?.assignment_limit || "Not available"}

**📖 FAQ-Based Answers:**
- **How to access Virtalibry?** Users log in via their **dashboard** where they manage books and reviews.
- **Who can access my book files?** Only **verified reviewers** who agree to review it.
- **Do Amazon reviews from Virtalibry count as Verified Purchase?** Only if a reviewer **buys the book** (not for Kindle Unlimited or direct PDFs).
- **What happens if a membership is canceled?** Reviews stay on Amazon; earned Virts remain if the user returns.
- **What is a Search & Buy Verified Purchase review?** Reviewers find the book **organically using a search query**, not a direct link.
- **How do Virts work?** Virts are used to **promote books in the library** and get them reviewed.
- **How many reviews will I get?** Depends on **genre, book quality, and assigned Virts**—there are no guarantees.
- **What makes Virtalibry different from review swapping?** Authors **never review each other directly**, ensuring unbiased feedback.
- **Who can use Virtalibry?** The platform is open to **international authors**, and they can review books in their region.
- **How do Photo Reviews work?** These reviews include **a book photo** and require a Verified Purchase.
- **What are the costs?** There is a **free plan** (one book) and **paid plans** for more books.

Always refer users to **official Virtalibry support** if they require advanced help, by email: support@virtalibry.com.`
                        },
                        { role: "user", content: input }, // User's message
                ],
            });

            setMessages([...messages, newMessage, { text: data.choices[0].message.content, sender: "bot" }]);
        } catch (error) {
            console.error("Message sending error:", error);
            setMessages([...messages, newMessage, { text: "Server error, please try again later.", sender: "bot" }]);
        }
    };

    // Handle Enter key press
    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            sendMessage();
        }
    };

    return (
        <div className="chat-widget">
            <div className="chat-header">
                <span>Support Chat</span>
                <button onClick={onClose}>X</button>
            </div>
            <div className="chat-body" style={{ display: "flex", flexDirection: "column" }}>
                {messages.map((msg, i) => (
                    <div key={i} className={`chat-message ${msg.sender}`}>
                        {msg.text}
                    </div>
                ))}
            </div>

            <div className="chat-footer">
                <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder="Type your message..."
                />
                <button onClick={sendMessage}>➤</button>
            </div>
        </div>
    );
};

export default ChatWidget;
